import PropTypes from 'prop-types'
import { useCallback, useReducer } from 'react'
import { capitalizeString } from '@commutifi-fe/utils'
import {
  DebouncedSearchActionTypes,
  DebouncedSearchState,
  EnhancedDebouncedSearchActionTypes,
  StatePropsUnion
} from './useDebounceSearchQuery/stateChangeTypes'

/**
 *
 * @param caller Caller component or function
 * @param propTypes PropTypes definition associated to the caller to validate
 *                  them by calling the callback (`validate`) function with the user props
 *
 * @returns {function} validate - callback function to call and validate propTypes
 */
export const getPropTypesValidator = (
  caller: (...args: any) => unknown,
  propTypes: Record<string, unknown>
) => {
  return function validate(options = {}) {
    Object.entries(propTypes).forEach(([key]) => {
      PropTypes.checkPropTypes(propTypes, options, key, caller.name)
    })
  }
}

/**
 * Get the default value (named default{NAME_OF_PROP}) associated with `propKey`
 * in the `props` passed to this function.
 * If it finds no default it will get the default value passed in the fallback
 * that the user passed down (`defaultStateLocalValues`)
 *
 * Default is used whenever we to set the default value to anytime downshift is reset.
 *
 * @param props Component props
 * @param propKey Prop to search for default value
 * @param defaultStateLocalValues Fallback default props object
 */
export const getDefaultValue = (
  props: any = {},
  propKey: string,
  defaultStateLocalValues: Record<string, unknown>
) => {
  const defaultPropKey = `default${capitalizeString(propKey, true)}`
  if (defaultPropKey in props) {
    return props[defaultPropKey]
  }
  return defaultStateLocalValues[propKey]
}

/**
 * Get the initial value (named initial{NAME_OF_PROP}) associated with `propKey`
 * in the `props` passed to this function.
 * If it finds no initial it will get the initial value passed in the fallback
 * that the user passed down (`defaultStateLocalValues`)
 *
 * Initial is used whenever we want to pass a prop that should be selected
 * when downshift is initialized
 *
 * @param props Component props
 * @param propKey Prop to search for default value
 * @param defaultStateLocalValues Fallback default props object
 */
export const getInitialValue = (
  props: Record<string, unknown> = {},
  propKey: string,
  defaultStateValuesLocal: Record<string, unknown>
) => {
  if (propKey in props) {
    return props[propKey]
  }
  const initialPropKey = `initial${capitalizeString(propKey, true)}`
  if (initialPropKey in props) {
    return props[initialPropKey]
  }
  return getDefaultValue(props, propKey, defaultStateValuesLocal)
}

/**
 * Get state by prioritizing the props passed down to the component
 * @param state
 * @param props
 */
const getState = (
  state: DebouncedSearchState,
  props: Partial<DebouncedSearchState>
): DebouncedSearchState =>
  (Object.keys(state || {}) as (keyof DebouncedSearchState)[]).reduce(
    (prevState, key) => {
      // Typescript can't understand that the end object should be a DebouncedSearchState...
      prevState[key] = (key in props ? props[key] : state?.[key]) as any
      return prevState
    },
    {} as DebouncedSearchState
  )

/**
 * Get original reducer to enhance the `stateReducer` property
 * with the original changes triggered by the component that control
 * the state
 * @param reducer Original reducer
 * @param initialState Initial reducer state
 * @param props
 */
export function useEnhancedReducer<T>(
  reducer: (
    state: DebouncedSearchState<T>,
    action: DebouncedSearchActionTypes<T> & { props: StatePropsUnion<T> }
  ) => DebouncedSearchState<T>,
  initialState: DebouncedSearchState<T>,
  props: Record<string, unknown>
): [
  DebouncedSearchState<T>,
  React.Dispatch<EnhancedDebouncedSearchActionTypes<T>>
] {
  const enhancedReducer = useCallback(
    (
      state: DebouncedSearchState<T>,
      action: EnhancedDebouncedSearchActionTypes<any>
    ) => {
      state = getState(state, action.props)
      const { stateReducer: stateReduceLocal } = action.props
      const changes = reducer(state, action)
      const newState = stateReduceLocal(state, { ...action, changes })
      return newState
    },
    [reducer]
  )

  const [state, dispatch] = useReducer(enhancedReducer, initialState)
  return [getState(state, props), dispatch]
}
